/**
 * Unsent message text per account, bond and bond request. Drafts live in memory for one browser
 * tab: sending or emptying the message clears its draft, logout clears them all, and a refresh
 * loses them. `useMessageDraft(key)` is the seam for persisting drafts (DEV-1568).
 */
import type { SuretyEntityIdMap } from "@portal/api/types";
import { isEmptyRichTextHtml } from "../../../utils/richTextHtml";

export type MessageDraftKey = `${keyof SuretyEntityIdMap}:${string}`;

export const messageDraftKey = <T extends keyof SuretyEntityIdMap>(
	type: T,
	id: SuretyEntityIdMap[T],
): MessageDraftKey => `${type}:${id}`;

const drafts = new Map<MessageDraftKey, string>();
const listeners = new Set<() => void>();

const notify = () => {
	for (const listener of listeners) listener();
};

export const messageDraftStore = {
	get: (key: MessageDraftKey) => drafts.get(key) ?? "",
	set: (key: MessageDraftKey, html: string) => {
		if (isEmptyRichTextHtml(html)) {
			if (!drafts.delete(key)) return;
		} else {
			if (drafts.get(key) === html) return;
			drafts.set(key, html);
		}
		notify();
	},
	clearAll: () => {
		if (drafts.size === 0) return;
		drafts.clear();
		notify();
	},
	subscribe: (listener: () => void) => {
		listeners.add(listener);
		return () => {
			listeners.delete(listener);
		};
	},
};
